import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Cookie, IceCream2, Store, ShoppingBag, Bike, CalendarDays } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import FacebookFeed from "../components/FacebookFeed";
import { BRAND, CATEGORIES } from "../lib/brand";
import { usePageMeta } from "../hooks/usePageMeta";
import { PAGE_SEO } from "../lib/seo";
import { getTodaysSpecial } from "../lib/specials";
import { DESSERT_OF_THE_WEEK } from "../lib/dessertOfTheWeek";
import DessertOfTheWeek from "../components/DessertOfTheWeek";
import TrailerStrip from "../components/TrailerStrip";

const Home = () => {
  // If a Dessert Of The Week is active, fold its name into the page title +
  // description so it lifts in search results without overwriting the base copy.
  const dotw = DESSERT_OF_THE_WEEK;
  const dotwActive = !!dotw?.active && !!dotw?.name && !/placeholder/i.test(dotw.name);
  const homeMeta = dotwActive
    ? {
        ...PAGE_SEO.home,
        title: `${dotw.name} · Dessert of the Week | Just Dessert Me`,
        description: `${dotw.name} — this week's hero dessert at Just Dessert Me Hamilton. ${PAGE_SEO.home.description}`,
      }
    : PAGE_SEO.home;
  usePageMeta(homeMeta);
  const [ecomFeatured, setEcomFeatured] = useState([]);
  const todaySpecial = getTodaysSpecial();

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      const ecom = data.filter((p) => !p.in_store_only);
      // Show 6 e-commerce features: 3 cookie pies + 3 loaded cookies.
      const pies = ecom.filter((p) => p.category === "cookie-pies").slice(0, 3);
      const loaded = ecom.filter((p) => p.category === "loaded-cookies").slice(0, 3);
      setEcomFeatured([...pies, ...loaded]);
    });
  }, []);

  return (
    <main data-testid="page-home">
      {/* Hamilton delivery banner */}
      <section
        data-testid="home-delivery-banner"
        className="relative border-b border-white/10 bg-gradient-to-r from-[#ff1a8c]/15 via-[#00b4ff]/15 to-[#ff1a8c]/15"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          <span className="flex items-center gap-2 text-sm text-gray-200">
            <Bike className="w-4 h-4 text-[#ff1a8c]" />
            <strong className="text-white font-semibold">Hamilton dessert delivery</strong>
            <span className="hidden sm:inline text-gray-300">
              — All of our amazing desserts, delivered to your door within the hour!
            </span>
          </span>
          {BRAND.delivery?.hamilton ? (
            <a
              href={BRAND.delivery.hamilton}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-delivery-cta"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#ff1a8c] text-white text-[0.72rem] uppercase tracking-[0.18em] font-semibold shadow-[0_0_18px_rgba(255,26,140,0.55)] hover:shadow-[0_0_28px_rgba(255,26,140,0.85)] hover:-translate-y-0.5 transition-all"
            >
              Order for Hamilton delivery <ArrowRight className="w-3.5 h-3.5" />
            </a>
          ) : (
            <span
              data-testid="home-delivery-cta-placeholder"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/15 text-[0.7rem] uppercase tracking-[0.2em] text-gray-400"
            >
              Order for Hamilton delivery · Link coming soon
            </span>
          )}
        </div>
      </section>

      {/* Today's Special banner */}
      {todaySpecial && (
        <section
          data-testid="home-todays-special-banner"
          className="border-b border-white/10 bg-[#050508]"
        >
          <Link
            to="/daily-specials"
            data-testid="home-todays-special-link"
            className="group block max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
          >
            <div className="flex items-center gap-4 min-w-0">
              <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#ff1a8c]/15 border border-[#ff1a8c]/60 text-[#ff1a8c]">
                <CalendarDays className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <p className="text-[0.65rem] uppercase tracking-[0.28em] text-[#00b4ff] font-bold">
                  {todaySpecial.label}&apos;s Special · On today
                </p>
                <p className="text-white font-semibold text-sm sm:text-base truncate">
                  {todaySpecial.title}
                </p>
              </div>
            </div>
            <span className="shrink-0 inline-flex items-center gap-1.5 text-[0.7rem] uppercase tracking-[0.22em] font-semibold text-[#ff1a8c] group-hover:text-white transition-colors">
              See all specials
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </span>
          </Link>
        </section>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage:
              "linear-gradient(to bottom, rgba(5,5,8,0.65), rgba(5,5,8,0.95)), url(https://images.pexels.com/photos/5820040/pexels-photo-5820040.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-16 lg:pt-20 lg:pb-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 flex flex-col gap-6 fade-up">
            <span className="inline-flex items-center gap-2 self-start text-[0.7rem] uppercase tracking-[0.3em] text-[#00b4ff]">
              <Sparkles className="w-3.5 h-3.5" /> Hamilton&apos;s family dessert destination
            </span>
            <h1
              data-testid="hero-headline"
              className="font-script text-6xl sm:text-7xl lg:text-[7.5rem] leading-[0.95] neon-gradient-text"
            >
              Just Dessert <br /> Me.
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl">
              Hamilton&apos;s home of rolled ice cream, waffles, pancakes, milkshakes and famous{" "}
              <span className="text-white">Cookie Pies</span> — made fresh in Hillcrest. Visit our
              dessert cafe, or order{" "}
              <span className="text-white">Loaded Cookies</span> online for dessert delivery in
              Hamilton and NZ-wide.
            </p>
            <div className="flex flex-wrap gap-4 mt-2">
              <Link to="/shop" data-testid="hero-shop-button" className="neon-btn">
                Shop Cookies <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/menus" data-testid="hero-menus-button" className="neon-btn neon-btn--blue">
                View Menus
              </Link>
            </div>
            <div className="flex items-center gap-8 mt-6 text-xs uppercase tracking-[0.2em] text-gray-400">
              <span>Open · {BRAND.hours}</span>
              <span className="hidden sm:inline">Dine-in & Online Orders</span>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up delay-2">
            <div className="relative aspect-square rounded-[2.5rem] overflow-hidden neon-frame-pink">
              <img
                src={BRAND.logo}
                alt={BRAND.name}
                className="w-full h-full object-cover bg-black flicker"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Dessert Of The Week */}
      <DessertOfTheWeek />

      <TrailerStrip />

      {/* Category strip */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <h2 className="font-script text-4xl sm:text-5xl text-white">Browse the menu</h2>
          <Link to="/menus" data-testid="browse-all-link" className="text-xs uppercase tracking-[0.25em] text-[#00b4ff] hover:text-white">
            See full menu →
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
          {CATEGORIES.map((c, idx) => {
            const Icon = c.inStoreOnly ? IceCream2 : Cookie;
            const Badge = c.inStoreOnly ? Store : ShoppingBag;
            const href = c.inStoreOnly ? "/menus" : `/shop?category=${c.id}`;
            return (
              <Link
                key={c.id}
                to={href}
                data-testid={`home-category-${c.id}`}
                className="group glass rounded-2xl p-5 text-center border border-white/10 hover:border-[#ff1a8c] hover:shadow-[0_0_24px_rgba(255,26,140,0.45)] transition-all"
              >
                <div className="mb-3">
                  <Icon className={`w-7 h-7 mx-auto ${idx % 2 === 0 ? "text-[#ff1a8c]" : "text-[#00b4ff]"}`} />
                </div>
                <p className="text-[0.78rem] uppercase tracking-[0.16em] text-gray-200 group-hover:text-white leading-tight">
                  {c.label}
                </p>
                <span className={`mt-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] ${c.inStoreOnly ? "text-[#ff1a8c]" : "text-[#00b4ff]"}`}>
                  <Badge className="w-2.5 h-2.5" /> {c.inStoreOnly ? "In store" : "In store & online"}
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured e-commerce products */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#00b4ff] mb-2">Order Online</p>
            <h2 className="font-script text-4xl sm:text-5xl text-white">Cookie Pies & Loaded Cookies</h2>
          </div>
          <Link to="/shop" data-testid="featured-shop-all-link" className="hidden sm:inline text-xs uppercase tracking-[0.25em] text-gray-300 hover:text-white">
            Shop all →
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {ecomFeatured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Storefront / Visit us */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="relative rounded-[2rem] overflow-hidden neon-frame-pink">
              <img
                src={BRAND.storefront}
                alt={`${BRAND.name} storefront`}
                data-testid="home-storefront-image"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#050508]/40 via-transparent to-transparent" />
            </div>
          </div>
          <div className="lg:col-span-5 fade-up delay-2">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ff1a8c] mb-3">Find us</p>
            <h2 className="font-script text-5xl sm:text-6xl text-white leading-[1] mb-6">
              Come Experience The<br />Uniqueness For Yourself.
            </h2>
            <p className="text-gray-300 leading-relaxed mb-6">
              Step inside our dessert bar for milkshakes, pancakes, waffles, rolled ice cream
              and a counter full of daily fresh treats. To completely understand just how fresh
              things are, you have to come and experience it for yourself.
            </p>
            <ul data-testid="home-specialties" className="flex flex-wrap gap-2 mb-8">
              {BRAND.specialties.map((s) => (
                <li
                  key={s}
                  className="px-4 py-2 rounded-full text-[0.72rem] uppercase tracking-[0.2em] border border-white/15 text-gray-200 hover:border-[#00b4ff] hover:text-white transition-all"
                >
                  {s}
                </li>
              ))}
            </ul>
            <div className="space-y-2 text-sm text-gray-300 mb-8">
              <p className="flex items-center gap-2"><span className="text-[#00b4ff] uppercase tracking-[0.2em] text-[0.7rem]">Where</span> {BRAND.address}</p>
              <p className="flex items-center gap-2"><span className="text-[#00b4ff] uppercase tracking-[0.2em] text-[0.7rem]">Hours</span> {BRAND.hours}</p>
            </div>
            <Link to="/contact" data-testid="home-storefront-contact-link" className="neon-btn neon-btn--blue">
              Get directions
            </Link>
          </div>
        </div>
      </section>

      {/* Promise bar */}
      <FacebookFeed />
    </main>
  );
};

export default Home;
